import { Collapse, Row } from 'antd';
import React from 'react';
import { isEmpty } from 'underscore';
import { useLocationAvailable } from '../../../../hooks/useLocationAvailable';
import useAuthStore from '../../../../store/authStore';
import useDatasetStore from '../../../../store/datasetStore';
import useHomeStore from '../../../../store/homeStore';
import { useActiveMarker } from '../../../../store/markerStore';
import { DownloadInfoItem } from '../../../../types/analytics';
import { useIsGlobalHDDtu, useIsTnw } from '../../../../utils/hooks';
import { getCategoryPostfix } from '../../../../utils/PointHelper';
import { Label } from '../../../common/Label/Label';
import DownloadDataModal from '../../../common/Modals/Download/DownloadDataModal';
import { useSelectedDataset } from '../../../layout/Header/components/SearchBar';
import { IWmsProps } from '../../Map/Layer/DatasetMesh';
import { useDatasetCart } from '../hooks/useDatasetCart';
import { useDatasetChange } from '../hooks/useDatasetChange';
import { useDatasetDownload } from '../hooks/useDatasetDownload';
import { useDatasetMetadata } from '../hooks/useDatasetMetadata';
import { useDatasetParameters } from '../hooks/useDatasetParameter';
import { useDatasetPrice } from '../hooks/useDatasetPrice';
import { useDatasetSpectraFeature } from '../hooks/useDatasetSpectraFeature';
import styles from '../Style/DatasetInfo.module.scss';
import { LocationInfoDisplay } from './Display/LocationInfoDisplay/LocationInfoDisplay';
import { PriceSection } from './Display/Price/PriceSection';
import { DatasetChangeWarningModal } from './Modal/DatasetWarning/DatasetChangeWarningModal';
import { DownloadOptionsModal } from './Modal/DownloadOptions/DownloadOptionsModal';
import { TNSWarningModal } from './Modal/TNSWarning/TNSWarningModal';
import { DateRangeSelector } from './Selector/DateRangeSelector/DateRangeSelector';
import { ModelDataSelector } from './Selector/ModelSelector/ModelDataSelector';

const DATASET_ENABLED_IN_SPECTRA_V2 = 'DWF23' as const;

export default function DatasetInfo(props: {
  item: DownloadInfoItem; //Best datasets of one categotry (Waves, Ocean, Atmosphere)
  coords: number[];
  markerId: string;
  onMeshControl: (value: string) => any;
  setMeshUrl: (item?: IWmsProps) => void;
  onRemoveProductFromCart: (pointName: string, category: string) => any;
  onAddProductToCart: (
    item: DownloadInfoItem,
    pointName: string,
    category: string,
    coordinates: number[]
  ) => Promise<boolean>;
  downloadItems: DownloadInfoItem[];
  label: string;
  disableDownload: boolean;
  setDisableDownload: React.Dispatch<React.SetStateAction<boolean>>;
  setDatasetIsOfflineWarningVisible: (value: boolean) => void;
  icon?: JSX.Element;
  namePanel: string;
}) {
  const setShowSpectraModal = useHomeStore(
    (state) => state.setShowSpectraModal
  );
  const timeStepInterval = useHomeStore((state) => state.timeStepInterval);
  const setTimeStepInterval = useHomeStore(
    (state) => state.setTimeStepInterval
  );
  const priceParameterSelection = useHomeStore(
    (state) => state.priceParameterSelection
  );
  const priceSpectreSelection = useHomeStore(
    (state) => state.priceSpectreSelection
  );
  const setPriceParameterSelection = useHomeStore(
    (state) => state.setPriceParameterSelection
  );
  const setPriceSpectreSelection = useHomeStore(
    (state) => state.setPriceSpectraSelection
  );
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const userGroupPermissions = useAuthStore(
    (state) => state.userGroupPermissions
  );
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const { onChange } = useSelectedDataset();

  const isTnw = useIsTnw(props.item?.datasetId);
  const isDTU = useIsGlobalHDDtu(props.item?.datasetId);

  const isDatasetEnabledInV2 = props.item.datasetId.includes(
    DATASET_ENABLED_IN_SPECTRA_V2
  );

  const point = useActiveMarker();
  const { price, priceLoading } = useDatasetPrice(
    props.item?.datasetId,
    props.item?.dateRange
  );

  const { cartLoading, addedToCart, handleAddToCart, handleRemoveFromCart } =
    useDatasetCart(
      props.item,
      props.coords,
      props.label,
      props.namePanel,
      price,
      isDTU,
      props.onAddProductToCart,
      props.onRemoveProductFromCart
    );

  const {
    downloadValues,
    downloadOptions,
    downloadType,
    isDownloadModalVisible,
    showWarn,
    checkPolicy,
    isDownloadSpectraModalVisible,
    showCancelDownloadButtonParameters,
    showCancelDownloadButton2DSpectre,
    handleChangeDownloadOptions,
    handlePolicyChange,
    handleDownloadModalCancel,
    setIsDownloadSpectraModalVisible,
    setShowWarn,
    handleDownload,
    handleCancelDownload,
    handleStartDownloadProcess,
    resetDownloadProcess,
  } = useDatasetDownload(
    props.item,
    props.coords,
    props.label,
    props.namePanel,
    price,
    isDatasetEnabledInV2,
    props.setDatasetIsOfflineWarningVisible
  );

  const { disabledDate } = useDatasetParameters(
    props.markerId,
    props.namePanel,
    props.coords,
    props.item?.datasetId
  );

  const {
    onDatasetChange,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    onDateChange,
    handleChangePanelCategory,
    isChangeDatasetWarningVisible,
  } = useDatasetChange(
    props.item,
    props.namePanel,
    props.downloadItems,
    props.onMeshControl,
    props.setMeshUrl
  );

  const {
    onShowMetadata,
    hasDatasets,
    datasetIsOffline,
    showDownloadButton,
    isTNSDatasetWarningVisible,
    setIsTNSDatasetWarningVisible,
  } = useDatasetMetadata(
    props.namePanel,
    props.coords,
    props.item,
    onDateChange,
    handleChangePanelCategory
  );

  const { show2DSpectra } = useDatasetSpectraFeature(
    props.coords,
    props.item?.datasetId,
    props.label
  );

  const isLocationAvailable = useLocationAvailable(
    props.namePanel,
    props.item?.datasetId,
    datasetBriefs,
    datasetIsOffline
  );

  return (
    <>
      <DatasetChangeWarningModal
        visible={isChangeDatasetWarningVisible}
        onOk={handleChangeDatasetWarningOk}
        onCancel={handleChangeDatasetWarningCancel}
      />

      <DownloadDataModal
        isVisible={isDownloadSpectraModalVisible}
        setIsVisible={setIsDownloadSpectraModalVisible}
        timeInfo={isDatasetEnabledInV2 ? undefined : '3 hours'}
      />

      <TNSWarningModal
        visible={isTNSDatasetWarningVisible}
        onClose={() => setIsTNSDatasetWarningVisible(false)}
      />

      <DownloadOptionsModal
        visible={isDownloadModalVisible}
        title='Download'
        options={downloadOptions}
        values={downloadValues}
        showWarn={showWarn}
        checkPolicy={checkPolicy}
        downloadType={downloadType}
        onCancel={handleDownloadModalCancel}
        onOk={() => {
          if (checkPolicy && downloadType) {
            handleDownload(downloadType, datasetIsOffline);
            resetDownloadProcess();
          } else {
            setShowWarn(true);
          }
        }}
        onChange={handleChangeDownloadOptions}
        onPolicyChange={handlePolicyChange}
        disableDownload={props.disableDownload}
        setDisableDownload={props.setDisableDownload}
        setShowWarn={setShowWarn}
      />
      <Collapse
        accordion
        activeKey={point?.activeDatasetKey}
        onChange={handleChangePanelCategory}
        className={`${styles.customCollapse} ${
          point?.activeDatasetKey === props.namePanel
            ? styles.activeDataset
            : ''
        }`}
      >
        <Collapse.Panel
          className={styles.datasetItem}
          header={
            <Label
              label={
                <div className={styles.DatasetCategory}>
                  {props.icon}
                  <div
                    className={styles.DatasetCategoryLabel}
                    id={`${props.namePanel}-panel`}
                  >
                    <h2>{props.label}</h2>
                    {getCategoryPostfix(props.label)}
                  </div>
                </div>
              }
            />
          }
          key={props.namePanel}
        >
          <Row gutter={16}>
            <ModelDataSelector
              id={`PointPanel-Dataset_Selection_${point?.label}_${props.namePanel}`}
              testId={`PointPanel-Dataset_Selection`}
              item={props?.item}
              datasetId={props.item?.datasetId}
              options={props.item?.options}
              onShowMetadata={onShowMetadata}
              onDatasetChange={onDatasetChange}
              isDisabled={addedToCart}
              onChange={onChange}
            />

            <DateRangeSelector
              id={`PointPanel-Date_Selection_${point?.label}_${props.namePanel}`}
              dateRange={props.item?.dateRange}
              disabledDate={disabledDate}
              onDateChange={(value) => onDateChange(value, false)}
              isDisabled={isEmpty(props.item?.datasetId) || addedToCart}
            />

            <LocationInfoDisplay
              hasDatasets={hasDatasets}
              isLocationAvailable={isLocationAvailable}
              waterDepth={point?.waterDepth?.[props.namePanel]}
              cellSize={point?.cellSize?.[props.namePanel]}
              landRelation={point?.landRelation?.[props.namePanel]}
              isWind={props.item?.radioKey === 'Wind'}
            />

            <PriceSection
              hasDatasets={hasDatasets}
              price={price}
              isTnw={isTnw}
              namePanel={props.namePanel}
              disableDownload={props.disableDownload}
              isLocationAvailable={isLocationAvailable}
              priceLoading={priceLoading}
              isDTU={isDTU}
              timeStepInterval={timeStepInterval}
              setTimeStepInterval={setTimeStepInterval}
              datasetId={props.item?.datasetId}
              addedToCart={addedToCart}
              showDownloadButton={showDownloadButton}
              showCancelDownloadButtonParameters={
                showCancelDownloadButtonParameters
              }
              showCancelDownloadButton2DSpectre={
                showCancelDownloadButton2DSpectre
              }
              show2DSpectra={show2DSpectra}
              isAuthenticated={isAuthenticated()}
              userId={point?.id}
              label={point?.label}
              userGroupPermissions={userGroupPermissions}
              handleAddToCart={handleAddToCart}
              handleRemoveFromCart={handleRemoveFromCart}
              handleStartDownloadProcess={handleStartDownloadProcess}
              handleCancelDownload={handleCancelDownload}
              setShowSpectraModal={setShowSpectraModal}
              priceParameterSelection={priceParameterSelection}
              setPriceParameterSelection={setPriceParameterSelection}
              priceSpectraSelection={priceSpectreSelection}
              setPriceSpectraSelection={setPriceSpectreSelection}
              cartLoading={cartLoading}
            />
          </Row>
        </Collapse.Panel>
      </Collapse>
    </>
  );
}
